/**
 * ProgrammeDetailPage (/programmes/:slug) — a single programme's detail page,
 * driven entirely from src/data/programmes.ts and @/config/brand. No fact,
 * figure or claim is invented here: every conditional block (curriculum,
 * specialisations, delivery modes, entry requirements, gallery, ACBSP proof,
 * retention) renders only when the data explicitly carries it, so an on-site
 * programme with sparse data still resolves to a clean, correct page with no
 * empty sections. The ACBSP mark/badge/retention appears ONLY where
 * programme.acbsp is true (the MBA & DBA routes), captioned per the claims rule;
 * the BBA's in-progress ACBSP effort is a text-only "at a glance" fact with no
 * mark. The SPECIALISATION_CAVEAT renders wherever a specialisation/route list
 * appears. Anything the data does not state routes the visitor to an adviser
 * rather than guessing.
 */
import {
  ArrowUpRight,
  BookOpen,
  Check,
  Globe,
  GraduationCap,
  Layers,
  type LucideIcon,
  MapPin,
  Video,
} from 'lucide-react';
import { Navigate, useParams, Link } from 'react-router-dom';

import { brand } from '@/config/brand';
import {
  getProgramme,
  programmeImage,
  PROGRAMMES,
  SPECIALISATION_CAVEAT,
} from '@/data/programmes';
import {
  AnimatedCounter,
  Magnetic,
  MediaFrame,
  NavyAtmosphere,
  Parallax,
  ScrollReveal,
  SplitReveal,
  TiltCard,
} from '@/components/motion';
import { ProgrammeCurriculum, ProgrammeGallery } from '@/components/sections';
import { Badge, Button, Card } from '@ibas/ui';

/** Small lucide glyph per delivery mode (falls back to a mortarboard). */
const DELIVERY_ICONS: Record<string, LucideIcon> = {
  'Self-study': BookOpen,
  'Live online': Video,
  Blended: Layers,
  'On-campus': MapPin,
  'Partner centre': Globe,
};

export function ProgrammeDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const programme = getProgramme(slug);

  // Unknown slug → back to the listing (never render an empty/fabricated page).
  if (!programme) {
    return <Navigate to="/programmes" replace />;
  }

  const others = PROGRAMMES.filter((p) => p.slug !== programme.slug);
  // Parse the published retention string ("97.3%") for the count-up. Only used
  // inside the acbsp-guarded block, so it never surfaces where it must not.
  const retentionValue = programme.retention2025
    ? Number.parseFloat(programme.retention2025)
    : null;
  // The masters route lists "16 specialisations" but has no Curriculum/
  // Specialisations section of its own, so the mandated caveat is appended to
  // the "at a glance" adviser note instead.
  const specialisationCaveatInGlance = programme.slug === 'masters';

  return (
    <div className="bg-background">
      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section className="relative">
        <MediaFrame
          image={programmeImage(programme, 1600)}
          alt={programme.alt}
          position={programme.imagePosition}
          aspect="aspect-[4/5] sm:aspect-video lg:aspect-[16/7]"
          overlay={68}
          reveal={false}
          rounded="rounded-none"
        >
          {/* White copy sits on the navy overlay for contrast. */}
          <div className="flex h-full items-end">
            <div className="mx-auto w-full max-w-6xl px-6 pb-10 md:pb-16">
              <div className="max-w-2xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="font-label tracking-wider uppercase"
                  >
                    {programme.type === 'On-site' ? (
                      <MapPin aria-hidden className="size-3.5" />
                    ) : (
                      <GraduationCap aria-hidden className="size-3.5" />
                    )}
                    {programme.type}
                    {programme.location ? ` · ${programme.location}` : ''}
                  </Badge>
                </div>

                <p className="mt-5 font-label text-xs tracking-[0.22em] text-brand-gold-light uppercase">
                  {programme.code}
                </p>

                <SplitReveal
                  as="h1"
                  by="lines"
                  className="mt-3 font-display text-4xl leading-tight text-white md:text-5xl lg:text-6xl"
                >
                  {programme.title}
                </SplitReveal>

                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                  {programme.summary}
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Magnetic>
                    <Button asChild size="lg" variant="secondary">
                      <Link to="/apply">Start your application</Link>
                    </Button>
                  </Magnetic>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  >
                    <Link to="/admissions#find-advisor">Connect with an adviser</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </MediaFrame>
      </section>

      {/* ── INTRO ─────────────────────────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-24">
        <div className="mx-auto w-full max-w-3xl px-6">
          <ScrollReveal from="up">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Overview
            </p>
          </ScrollReveal>
          <SplitReveal
            as="p"
            by="lines"
            className="mt-4 font-display text-2xl leading-snug text-foreground md:text-3xl"
          >
            {programme.intro}
          </SplitReveal>
        </div>
      </section>

      {/* ── AT A GLANCE (+ entry requirements) ─────────────────────────────── */}
      <section className="bg-muted/40 py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <header className="mb-10 max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              At a glance
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              The essentials, verified.
            </SplitReveal>
          </header>

          <ScrollReveal
            from="up"
            stagger={0.08}
            className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3"
          >
            {programme.facts.map((fact) => (
              <div key={fact.label} className="bg-background p-6 md:p-8">
                <p className="font-label text-xs tracking-[0.16em] text-muted-foreground uppercase">
                  {fact.label}
                </p>
                <p className="mt-2 font-display text-xl leading-tight text-foreground md:text-2xl">
                  {fact.value}
                </p>
              </div>
            ))}
          </ScrollReveal>

          {/* Entry requirements — renders only where verified (self-hides masters). */}
          {programme.entryRequirements && (
            <ScrollReveal
              from="up"
              delay={0.1}
              className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2"
            >
              {programme.entryRequirements.map((req) => (
                <div key={req.label} className="bg-background p-6 md:p-8">
                  <p className="font-label text-xs tracking-[0.16em] text-brand-gold uppercase">
                    {`Entry · ${req.label}`}
                  </p>
                  <p className="mt-2 text-base leading-relaxed text-foreground">
                    {req.detail}
                  </p>
                </div>
              ))}
            </ScrollReveal>
          )}

          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Not seeing the detail you need — intake dates, tuition or anything else?{' '}
              <Link
                to="/admissions#find-advisor"
                className="font-medium text-foreground underline decoration-brand-gold/60 underline-offset-4 hover:decoration-brand-gold"
              >
                Speak with an adviser
              </Link>
              .
            </p>
            {specialisationCaveatInGlance && (
              <p className="mt-3 max-w-2xl border-l-2 border-brand-gold/40 pl-4 text-xs leading-relaxed text-muted-foreground">
                {SPECIALISATION_CAVEAT}
              </p>
            )}
          </ScrollReveal>
        </div>
      </section>

      {/* ── HIGHLIGHTS ────────────────────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-24">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-16">
          <div>
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Why this programme
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              {brand.campaignLine}
            </SplitReveal>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
              {brand.promise.join(' ')}
            </p>
          </div>

          <ScrollReveal from="up" stagger={0.08} as="ul" className="flex flex-col gap-4">
            {programme.highlights.map((highlight) => (
              <li
                key={highlight}
                className="flex items-start gap-4 rounded-xl border border-border bg-muted/30 p-5"
              >
                <span
                  aria-hidden
                  className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-navy text-white"
                >
                  <Check className="size-4" />
                </span>
                <p className="text-base leading-relaxed text-foreground">{highlight}</p>
              </li>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ── CURRICULUM (conditional) ──────────────────────────────────────── */}
      {programme.curriculum && (
        <ProgrammeCurriculum
          groups={programme.curriculum}
          lead={programme.curriculumLead}
          caveat={programme.curriculumHasSpecialisations ? SPECIALISATION_CAVEAT : undefined}
        />
      )}

      {/* ── WAYS TO STUDY (conditional — online programmes) ────────────────── */}
      {programme.deliveryModes && (
        <section className="bg-background py-16 md:py-24">
          <div className="mx-auto w-full max-w-6xl px-6">
            <header className="mb-10 max-w-2xl">
              <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
                Ways to study
              </p>
              <SplitReveal
                as="h2"
                by="lines"
                className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
              >
                Study your way.
              </SplitReveal>
            </header>

            <ScrollReveal
              from="up"
              stagger={0.06}
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {programme.deliveryModes.map((mode) => {
                const Icon = DELIVERY_ICONS[mode.label] ?? GraduationCap;
                return (
                  <div
                    key={mode.label}
                    className="flex flex-col gap-3 rounded-2xl border border-border bg-muted/30 p-6"
                  >
                    <span
                      aria-hidden
                      className="flex size-10 items-center justify-center rounded-full bg-brand-navy text-brand-gold-light"
                    >
                      <Icon className="size-5" />
                    </span>
                    <h3 className="font-display text-lg font-semibold text-foreground md:text-xl">
                      {mode.label}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {mode.detail}
                    </p>
                  </div>
                );
              })}
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ── SPECIALISATIONS (conditional — Flexi-MBA only) ────────────────── */}
      {programme.specialisations && (
        <section className="bg-muted/40 py-16 md:py-24">
          <div className="mx-auto w-full max-w-6xl px-6">
            <header className="mb-10 max-w-2xl">
              <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
                Specialisations
              </p>
              <SplitReveal
                as="h2"
                by="lines"
                className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
              >
                16 specialisations across four tracks.
              </SplitReveal>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                Shape the programme around where you want to lead — personalise your
                route by choosing a specialisation within one of four tracks.
              </p>
            </header>

            <ScrollReveal
              from="up"
              stagger={0.08}
              className="grid grid-cols-1 gap-6 sm:grid-cols-2"
            >
              {programme.specialisations.map((group) => (
                <TiltCard key={group.track} max={5} className="h-full">
                  <Card className="flex h-full flex-col gap-4 border-border p-6 md:p-7">
                    <h3 className="font-display text-xl font-semibold text-foreground md:text-2xl">
                      {group.track}
                    </h3>
                    <ul className="flex flex-col gap-2.5">
                      {group.options.map((option) => (
                        <li
                          key={option}
                          className="flex items-center gap-3 text-sm leading-relaxed text-muted-foreground"
                        >
                          <span
                            aria-hidden
                            className="size-1.5 shrink-0 rounded-full bg-brand-gold"
                          />
                          {option}
                        </li>
                      ))}
                    </ul>
                  </Card>
                </TiltCard>
              ))}
            </ScrollReveal>

            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-8 max-w-3xl border-l-2 border-brand-gold/40 pl-4 text-xs leading-relaxed text-muted-foreground">
                {SPECIALISATION_CAVEAT}
              </p>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ── CAMPUS & COMMUNITY GALLERY (conditional — ≥3 tiles) ────────────── */}
      {programme.gallery && (
        <ProgrammeGallery items={programme.gallery} type={programme.type} />
      )}

      {/* ── ACBSP PROOF (conditional — acbsp === true) ────────────────────── */}
      {programme.acbsp && (
        <section className="relative isolate overflow-hidden bg-brand-navy py-20 md:py-28">
          <NavyAtmosphere />
          <div className="relative z-10 mx-auto w-full max-w-6xl px-6">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
              <div>
                <p className="font-label text-xs tracking-[0.22em] text-brand-gold-light uppercase">
                  Programme accreditation
                </p>
                <SplitReveal
                  as="h2"
                  by="lines"
                  className="mt-3 font-display text-3xl leading-tight text-white md:text-4xl"
                >
                  ACBSP-accredited quality.
                </SplitReveal>
                <p className="mt-5 max-w-md text-base leading-relaxed text-white/80">
                  This programme carries accreditation from the Accreditation Council
                  for Business Schools and Programs (ACBSP), which is recognised by the
                  Council for Higher Education Accreditation (CHEA) in the United States.
                </p>

                <div className="mt-7 flex flex-wrap items-center gap-6">
                  <span className="inline-flex rounded-xl bg-white p-3">
                    <img
                      src="/media/images/accreditation/acbsp-accredited-program-badge.jpg"
                      alt="ACBSP Accredited Program badge."
                      loading="lazy"
                      className="h-16 w-auto"
                    />
                  </span>
                  <Link
                    to="/experience/student-achievement-rates"
                    className="group inline-flex items-center gap-1.5 font-medium text-brand-gold-light underline decoration-brand-gold/50 underline-offset-4 hover:decoration-brand-gold-light"
                  >
                    See the full ACBSP figures
                    <ArrowUpRight
                      aria-hidden
                      className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transition-none"
                    />
                  </Link>
                </div>
              </div>

              {retentionValue !== null && (
                <ScrollReveal from="up">
                  <Parallax speed={0.12}>
                    <div className="rounded-2xl border border-white/15 bg-white/5 p-8 text-center md:p-10">
                      <p className="font-display text-6xl leading-none text-brand-gold-light md:text-7xl">
                        <AnimatedCounter
                          value={retentionValue}
                          decimals={1}
                          suffix="%"
                        />
                      </p>
                      <p className="mt-4 font-label text-xs tracking-[0.16em] text-white/70 uppercase">
                        Student retention 2025 (ACBSP)
                      </p>
                    </div>
                  </Parallax>
                </ScrollReveal>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── EXPLORE OTHER PROGRAMMES ──────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <header className="mb-8 max-w-2xl">
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Keep exploring
            </p>
            <SplitReveal
              as="h2"
              by="lines"
              className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
            >
              Other IBAS programmes.
            </SplitReveal>
          </header>

          <ScrollReveal
            from="up"
            stagger={0.06}
            as="ul"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            {others.map((other) => (
              <li key={other.slug}>
                <Link
                  to={`/programmes/${other.slug}`}
                  className="group flex items-center justify-between gap-4 rounded-xl border border-border bg-muted/30 p-5 transition-colors duration-300 hover:border-brand-gold/40 hover:bg-muted/60 motion-reduce:transition-none"
                >
                  <div>
                    <p className="font-label text-xs tracking-[0.16em] text-brand-gold uppercase">
                      {other.code}
                    </p>
                    <p className="mt-1 font-display text-lg font-semibold text-foreground md:text-xl">
                      {other.title}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {other.type}
                      {other.location ? ` · ${other.location}` : ''}
                    </p>
                  </div>
                  <ArrowUpRight
                    aria-hidden
                    className="size-5 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-gold motion-reduce:transition-none"
                  />
                </Link>
              </li>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* ── CLOSING CTA ───────────────────────────────────────────────────── */}
      <section className="relative isolate overflow-hidden bg-brand-navy py-20 md:py-28">
        <NavyAtmosphere />
        <div className="relative z-10 mx-auto w-full max-w-3xl px-6 text-center">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold-light uppercase">
            Take the next step
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-white md:text-5xl"
          >
            {brand.supportingPhrase}
          </SplitReveal>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/80">
            Start your application today, or talk to an adviser about intakes, entry
            requirements and fees — we’ll help you find the right route.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Magnetic>
              <Button asChild size="lg" variant="secondary">
                <Link to="/apply">Start your application</Link>
              </Button>
            </Magnetic>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <Link to="/admissions#find-advisor">Connect with an adviser</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
